import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ImagePlus, X, Users, Download, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate, TEAM_LABELS } from "../../lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PART_STATUTS = {
  "DEMANDE": { label: "Demande", cls: "bg-amber-100 text-amber-700" },
  "VALIDÉE": { label: "Validée", cls: "bg-navy text-white" },
  "CONFIRMÉE": { label: "Confirmée", cls: "bg-green-100 text-green-700" },
};

function ParticipantsPanel({ event }) {
  const [parts, setParts] = useState(null);

  const load = () => api.get(`/admin/participations?event_id=${event.id}`).then((r) => setParts(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [event.id]);

  const setStatut = async (p, statut) => {
    try {
      await api.patch(`/admin/participations/${p.id}`, { statut });
      toast.success(`Participation ${PART_STATUTS[statut].label.toLowerCase()}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/events/${event.id}/participants/export`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `participants-${event.name}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (parts === null) return <div className="px-6 py-4"><Loader2 className="animate-spin text-rouge" size={20} /></div>;

  return (
    <div className="bg-mist border-t border-navy/10 px-6 py-5" data-testid={`participants-panel-${event.id.slice(0, 8)}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="font-heading uppercase tracking-wider text-xs text-navy/70">Demandes de participation ({parts.length})</p>
        <button onClick={exportCsv} data-testid={`participants-export-${event.id.slice(0, 8)}`} className="inline-flex items-center gap-2 border border-navy/25 text-navy text-xs font-heading uppercase tracking-wider px-4 py-2 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
          <Download size={13} /> Exporter les inscrits
        </button>
      </div>
      {parts.length === 0 ? (
        <p className="text-sm text-steel">Aucune demande de participation pour cette course.</p>
      ) : (
        parts.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 py-3 border-b border-navy/10 last:border-0" data-testid={`participation-row-${p.id.slice(0, 8)}`}>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 ${PART_STATUTS[p.statut].cls}`}>{PART_STATUTS[p.statut].label}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-navy">{p.prenom} {p.nom}</p>
              <p className="text-xs text-steel">{p.email} · demande du {fmtDate(p.created_at)}</p>
            </div>
            <div className="flex gap-2">
              {p.statut === "DEMANDE" && (
                <button onClick={() => setStatut(p, "VALIDÉE")} data-testid={`participation-validate-${p.id.slice(0, 8)}`} className="inline-flex items-center gap-1.5 text-xs font-heading uppercase tracking-wider bg-navy text-white px-3 py-2 hover:bg-rouge transition-colors">
                  <CheckCircle2 size={13} /> Valider
                </button>
              )}
              {p.statut === "VALIDÉE" && (
                <button onClick={() => setStatut(p, "CONFIRMÉE")} data-testid={`participation-confirm-${p.id.slice(0, 8)}`} className="inline-flex items-center gap-1.5 text-xs font-heading uppercase tracking-wider bg-green-600 text-white px-3 py-2 hover:bg-navy transition-colors">
                  <ShieldCheck size={13} /> Confirmer l'inscription
                </button>
              )}
              {p.statut !== "DEMANDE" && (
                <button onClick={() => setStatut(p, "DEMANDE")} data-testid={`participation-reset-${p.id.slice(0, 8)}`} className="text-xs text-steel hover:text-rouge transition-colors underline">
                  Repasser en demande
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const EMPTY_EVENT = {
  date: "", name: "", location: "", comarque: "", category: "Course sur route",
  race_type: "", categories: "", team: "", status: "À venir",
  start_address: "", license_check_time: "", start_time: "",
  distance_km: "", circuit_km: "", official_url: "", registration_url: "",
  registration_deadline: "", registration_info: "", photo: "", info: "",
};

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

function Field({ label, id, testId, children }) {
  return (
    <div>
      <label className={labelCls} htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

export default function AdminCalendrier() {
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [eventCategories, setEventCategories] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/events");
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/meta/categories").then((r) => setEventCategories(r.data.event_categories)).catch(() => {});
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_EVENT);
    window.scrollTo({ top: 0 });
  };

  const startEdit = (ev) => {
    setEditing(ev.id);
    setForm({ ...EMPTY_EVENT, ...Object.fromEntries(Object.keys(EMPTY_EVENT).map((k) => [k, ev[k] ?? EMPTY_EVENT[k]])) });
    window.scrollTo({ top: 0 });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/events", form);
        toast.success("Course créée");
      } else {
        await api.put(`/admin/events/${editing}`, form);
        toast.success("Course mise à jour");
      }
      setEditing(null);
      setForm(EMPTY_EVENT);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Supprimer la course « ${ev.name} » ?`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`);
      toast.success("Course supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd);
      setForm((f) => ({ ...f, photo: data.path }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <p className="text-steel text-sm" data-testid="events-count">{events.length} course{events.length > 1 ? "s" : ""}</p>
        <button
          onClick={startNew}
          data-testid="admin-new-event"
          className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-6 py-3 hover:bg-navy transition-colors duration-300"
        >
          <Plus size={16} /> Nouvelle course
        </button>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="event-editor" className="bg-white border border-navy/10 p-6 sm:p-8 mb-10 space-y-5">
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl">
            {editing === "new" ? "Nouvelle course" : "Modifier la course"}
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Field label="Nom de la course *" id="ev-name">
                <input id="ev-name" data-testid="event-name" required value={form.name} onChange={set("name")} className={inputCls} />
              </Field>
            </div>
            <Field label="Date *" id="ev-date">
              <input id="ev-date" data-testid="event-date" type="date" required value={form.date} onChange={set("date")} className={inputCls} />
            </Field>
            <Field label="Équipe concernée" id="ev-team">
              <select id="ev-team" data-testid="event-team" value={form.team} onChange={set("team")} className={inputCls}>
                <option value="">—</option>
                {Object.entries(TEAM_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Lieu" id="ev-location">
              <input id="ev-location" data-testid="event-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="Ulldecona" />
            </Field>
            <Field label="Comarque / département / région" id="ev-comarque">
              <input id="ev-comarque" data-testid="event-comarque" value={form.comarque} onChange={set("comarque")} className={inputCls} placeholder="Montsià – Tarragona" />
            </Field>
            <Field label="Type de course" id="ev-race-type">
              <input id="ev-race-type" data-testid="event-race-type" value={form.race_type} onChange={set("race_type")} className={inputCls} placeholder="Course en ligne" />
            </Field>
            <Field label="Catégories" id="ev-categories">
              <input id="ev-categories" data-testid="event-categories" value={form.categories} onChange={set("categories")} className={inputCls} placeholder="Élite / U23" />
            </Field>
            <Field label="Catégorie (filtre du site)" id="ev-category">
              <select id="ev-category" data-testid="event-category" value={form.category} onChange={set("category")} className={inputCls}>
                {eventCategories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Statut affiché" id="ev-status">
              <input id="ev-status" data-testid="event-status" value={form.status} onChange={set("status")} className={inputCls} placeholder="À venir" />
            </Field>
            <Field label="Adresse de départ" id="ev-start-address">
              <input id="ev-start-address" data-testid="event-start-address" value={form.start_address} onChange={set("start_address")} className={inputCls} placeholder="Carrer Major" />
            </Field>
            <div className="grid grid-cols-2 gap-5">
              <Field label="Contrôle licences" id="ev-license-time">
                <input id="ev-license-time" data-testid="event-license-time" type="time" value={form.license_check_time} onChange={set("license_check_time")} className={inputCls} />
              </Field>
              <Field label="Heure départ" id="ev-start-time">
                <input id="ev-start-time" data-testid="event-start-time" type="time" value={form.start_time} onChange={set("start_time")} className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <Field label="Distance" id="ev-distance">
                <input id="ev-distance" data-testid="event-distance" value={form.distance_km} onChange={set("distance_km")} className={inputCls} placeholder="136 km" />
              </Field>
              <Field label="Distance du circuit" id="ev-circuit">
                <input id="ev-circuit" data-testid="event-circuit" value={form.circuit_km} onChange={set("circuit_km")} className={inputCls} placeholder="34 km" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Lien officiel (site de la course)" id="ev-official-url">
                <input id="ev-official-url" data-testid="event-official-url" type="url" value={form.official_url} onChange={set("official_url")} className={inputCls} placeholder="https://…" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Lien d'inscription officiel (facultatif)" id="ev-registration-url">
                <input id="ev-registration-url" data-testid="event-registration-url" type="url" value={form.registration_url} onChange={set("registration_url")} className={inputCls} placeholder="https://… (plateforme d'inscription de l'organisateur)" />
              </Field>
            </div>
            <Field label="Date limite d'inscription (facultatif)" id="ev-deadline">
              <input id="ev-deadline" data-testid="event-deadline" type="date" value={form.registration_deadline} onChange={set("registration_deadline")} className={inputCls} />
            </Field>
            <Field label="Informations pour l'inscription (facultatif)" id="ev-reginfo">
              <input id="ev-reginfo" data-testid="event-reginfo" value={form.registration_info} onChange={set("registration_info")} className={inputCls} placeholder="Inscription réservée aux coureurs sélectionnés…" />
            </Field>
            <div className="sm:col-span-2">
              <span className={labelCls}>Photo (optionnelle)</span>
              {form.photo ? (
                <div className="flex items-center gap-4" data-testid="event-photo-preview">
                  <img src={`${API_BASE}/api/files/${form.photo}`} alt="Aperçu" className="h-24 w-40 object-cover border border-navy/10" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, photo: "" })}
                    data-testid="event-photo-remove"
                    className="inline-flex items-center gap-2 border border-navy/25 text-navy text-xs font-heading uppercase tracking-wider px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
                  >
                    <X size={14} /> Retirer
                  </button>
                </div>
              ) : (
                <label
                  data-testid="event-photo-upload"
                  className="flex items-center justify-center gap-3 border border-dashed border-navy/25 px-6 py-8 cursor-pointer text-steel text-sm hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                  {uploading ? "Téléversement…" : "Choisir une image (jpg, png, webp — 8 Mo max)"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  />
                </label>
              )}
            </div>
            <div className="sm:col-span-2">
              <Field label="Informations complémentaires" id="ev-info">
                <textarea id="ev-info" data-testid="event-info" rows={4} value={form.info} onChange={set("info")} className={inputCls} placeholder="Programme, modalités d'inscription, remarques…" />
              </Field>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving || uploading} data-testid="event-save" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
              {saving && <Loader2 size={15} className="animate-spin" />}
              Enregistrer
            </button>
            <button type="button" onClick={() => setEditing(null)} data-testid="event-cancel" className="border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
              Annuler
            </button>
          </div>
        </form>
      )}

      {busy ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
      ) : events.length === 0 ? (
        <div className="border border-dashed border-navy/25 px-8 py-16 text-center" data-testid="events-empty">
          <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Aucune course</p>
          <p className="mt-2 text-sm text-steel">Ajoutez la première course du calendrier avec le bouton « Nouvelle course ».</p>
        </div>
      ) : (
        <div className="bg-white border border-navy/10" data-testid="events-table">
          {events.map((ev) => (
            <div key={ev.id} data-testid={`event-row-${ev.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-navy/10">
                {ev.photo ? (
                  <img src={`${API_BASE}/api/files/${ev.photo}`} alt="" className="shrink-0 h-12 w-20 object-cover border border-navy/10" />
                ) : null}
                <div className="min-w-0 flex-1">
                  <p className="font-heading uppercase font-semibold text-navy truncate">{ev.name}</p>
                  <p className="text-xs text-steel">
                    {fmtDate(ev.date)} · {ev.location || "Lieu à confirmer"}{ev.categories ? ` · ${ev.categories}` : ""}{ev.team ? ` · ${TEAM_LABELS[ev.team] || ev.team}` : ""}
                  </p>
                  <p className="text-[11px] text-steel mt-1" data-testid={`event-counts-${ev.id.slice(0, 8)}`}>
                    Demandes : <strong className="text-navy">{ev.participations?.demandes ?? 0}</strong> ·
                    Validées : <strong className="text-navy">{ev.participations?.validees ?? 0}</strong> ·
                    Confirmées : <strong className="text-green-700">{ev.participations?.confirmees ?? 0}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}
                    data-testid={`participants-toggle-${ev.id.slice(0, 8)}`}
                    className="p-2 text-navy/50 hover:text-rouge transition-colors"
                    title="Participants"
                    aria-label="Voir les participants"
                  >
                    <Users size={16} />
                  </button>
                  <button onClick={() => startEdit(ev)} data-testid={`edit-event-${ev.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Modifier">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => remove(ev)} data-testid={`delete-event-${ev.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {expanded === ev.id && <ParticipantsPanel event={ev} />}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
